import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useLocation from "routing/useLocation";
import useThemeMode from "theme/hooks/useThemeMode";
import { manager } from "./tourManagerInstance";
import { resolveTargetWithRetry } from "./resolveTarget";
import { routeSatisfies } from "./stepConditions/routeMatch";
import useVerascendTourConfig from "./useVerascendTourConfig";
import {
  Card,
  Arrow,
  Header,
  Eyebrow,
  CloseButton,
  Title,
  Body,
  Footer,
  Dots,
  Dot,
  Actions,
  SkipButton,
  NextButton,
  calloutThemeFor
} from "./TourCallout.styles";
const CARD_WIDTH = 380;
const VIEWPORT_MARGIN = 16;
const ARROW_GAP = 12;
const ACCENT = "#1D5BD8";
function useElementRect(targetEl) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!targetEl) return void 0;
    let frame;
    let lastRect = null;
    const tick = () => {
      const r = targetEl.getBoundingClientRect();
      const next = { top: r.top, left: r.left, width: r.width, height: r.height };
      if (!lastRect || lastRect.top !== next.top || lastRect.left !== next.left || lastRect.width !== next.width || lastRect.height !== next.height) {
        lastRect = next;
        setRect(next);
      }
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [targetEl]);
  return targetEl ? rect : null;
}
const ARROW_HALF_WIDTH = 8;
const ARROW_EDGE_MARGIN = 20;
function calloutPosition(rect) {
  const spaceBelow = window.innerHeight - (rect.top + rect.height);
  const placement = spaceBelow >= rect.top ? "bottom" : "top";
  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - CARD_WIDTH / 2, VIEWPORT_MARGIN),
    window.innerWidth - CARD_WIDTH - VIEWPORT_MARGIN
  );
  const style = placement === "bottom" ? { position: "fixed", top: rect.top + rect.height + ARROW_GAP, left } : { position: "fixed", bottom: window.innerHeight - rect.top + ARROW_GAP, left };
  const targetCenterInCard = rect.left + rect.width / 2 - left - ARROW_HALF_WIDTH;
  const arrowOffset = Math.min(
    Math.max(targetCenterInCard, ARROW_EDGE_MARGIN),
    CARD_WIDTH - ARROW_EDGE_MARGIN - ARROW_HALF_WIDTH * 2
  );
  return { placement, style, arrowOffset };
}
export default function TourOverlay() {
  const { enabled: tourEnabled } = useVerascendTourConfig();
  const location = useLocation();
  const { currentMode } = useThemeMode();
  const [state, setState] = useState(() => manager.getState());
  const [resolvedTarget, setResolvedTarget] = useState({
    step: null,
    el: null
  });
  useEffect(() => manager.subscribe(setState), []);
  useEffect(() => {
    if (tourEnabled) {
      manager.start();
    }
  }, [tourEnabled]);
  useEffect(() => {
    let cancelled = false;
    const forStep = state.step;
    if (!forStep) return void 0;
    resolveTargetWithRetry(forStep.target).then((el) => {
      if (cancelled) return;
      setResolvedTarget({ step: forStep, el });
      requestAnimationFrame(() => el == null ? void 0 : el.scrollIntoView({ block: "nearest", inline: "center" }));
    });
    return () => {
      cancelled = true;
    };
  }, [state.step, location.pathname]);
  const targetEl = resolvedTarget.step === state.step ? resolvedTarget.el : null;
  useEffect(() => {
    var _a;
    if (!state.active || !((_a = state.step) == null ? void 0 : _a.activeOn)) return;
    const onScript = routeSatisfies({ type: "route", pathIncludes: state.step.activeOn.pathIncludes });
    if (!onScript) {
      manager.stop();
    }
  }, [state.active, state.step, location.pathname]);
  const rect = useElementRect(targetEl);
  if (!tourEnabled || !state.active || !state.step) return null;
  const isCentered = !state.step.target;
  const c = calloutThemeFor(currentMode);
  const {
    placement,
    style: positionStyle,
    arrowOffset
  } = !isCentered && rect ? calloutPosition(rect) : { placement: "none", style: void 0, arrowOffset: void 0 };
  return createPortal(
    /* @__PURE__ */ React.createElement(
      "div",
      {
        style: {
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          pointerEvents: "none"
        }
      },
      !isCentered && rect && /* @__PURE__ */ React.createElement(
        "div",
        {
          style: {
            position: "fixed",
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            // Per user feedback (2026-11-03): in dark mode, a small icon target
            // (e.g. step 2's/5's) was hard to pick out — the dimmed backdrop has
            // no actual border of its own, so the only cue was "normal brightness
            // meets 55%-black", which barely registers for a small, already-dim
            // icon. Stacking two more box-shadow layers on top of the existing
            // dimming one (first in the list, so the later ones paint over its
            // edge) adds a real accent-colored ring: a crisp 2px line plus a soft
            // glow, using the callout's own `eyebrow` color so it's already
            // theme-tuned for contrast (darker blue in light mode, a lighter one
            // in dark mode against the dim backdrop) instead of a separate color
            // to keep in sync.
            boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 0 2px ${c.eyebrow}, 0 0 12px 3px ${c.eyebrow}80`,
            borderRadius: 4,
            pointerEvents: "none"
          }
        }
      ),
      /* @__PURE__ */ React.createElement(
        "div",
        {
          style: isCentered ? { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "auto" } : { ...positionStyle, pointerEvents: "auto" }
        },
        /* @__PURE__ */ React.createElement(Card, { $c: c }, /* @__PURE__ */ React.createElement(Arrow, { $c: c, $placement: placement, $offset: arrowOffset }), /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement(Eyebrow, { $c: c }, "Step ", state.stepIndex + 1, " of ", state.totalSteps), /* @__PURE__ */ React.createElement(CloseButton, { $c: c, type: "button", "aria-label": "Close tour", onClick: () => manager.skip() }, /* @__PURE__ */ React.createElement("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.2", strokeLinecap: "round", "aria-hidden": "true" }, /* @__PURE__ */ React.createElement("path", { d: "M6 6l12 12M18 6L6 18" })))), state.step.title && /* @__PURE__ */ React.createElement(Title, { $c: c }, state.step.title), /* @__PURE__ */ React.createElement(Body, { $c: c }, state.step.narrative), /* @__PURE__ */ React.createElement(Footer, null, /* @__PURE__ */ React.createElement(Dots, { "aria-hidden": "true" }, Array.from({ length: state.totalSteps }, (_, i) => /* @__PURE__ */ React.createElement(Dot, { key: i, $c: c, $active: i === state.stepIndex }))), /* @__PURE__ */ React.createElement(Actions, null, /* @__PURE__ */ React.createElement(SkipButton, { $c: c, type: "button", onClick: () => manager.skip() }, "Skip tour"), state.step.advanceOn.type === "manual" && /* @__PURE__ */ React.createElement(NextButton, { $accent: ACCENT, type: "button", onClick: () => manager.advanceManually() }, state.stepIndex === state.totalSteps - 1 ? "Finish" : "Next", /* @__PURE__ */ React.createElement(
          "svg",
          {
            width: "16",
            height: "16",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "2.4",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            "aria-hidden": "true"
          },
          /* @__PURE__ */ React.createElement("path", { d: "M5 12h14M13 6l6 6-6 6" })
        )))))
      )
    ),
    document.body
  );
}
